import { FormType } from '@/api/enum/formType.enum';
import AppFieldView from '@/common/components/form-view/AppFieldView';
import { showLabel } from '@/utils/helper';
import {
	Box,
	Button,
	CardContent,
	Chip,
	Grid,
	Typography,
} from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import Divider from '@mui/material/Divider';
import dayjs from 'dayjs';
import { DISPLAY_DATETIME_12H_FORMAT } from '@/utils/dateHelper';
import { UserDetailDTO } from '@/api/DTO/user/userList.interface';
import ChangeEmailModal from './ChangeEmailModal';
import ChangePhoneModal from './ChangePhoneModal';
import CreateOutlinedIcon from '@mui/icons-material/CreateOutlined';
import EditUserInfoForm from './EditUserInfoForm';

interface Props {
	userDetail: UserDetailDTO;
}
export default function UserDetail(props: Props) {
	const { userDetail } = props;
	const { t } = useTranslation();
	const [type, setType] = useState<FormType>(FormType.view);
	const { id } = useParams();

	const ageList = [
		{ id: 0, name: '<25' },
		{ id: 1, name: '25-34' },
		{ id: 2, name: '35-44' },
		{ id: 3, name: '45-54' },
		{ id: 4, name: '>54' },
	];
	const sexList = [
		{ id: 0, name: t('user.boy') },
		{ id: 1, name: t('user.girl') },
		{ id: 2, name: t('user.guarded') },
	];
	const booleanMap = {
		true: t('common.yes'),
		false: t('common.no'),
	};
	const statusList = [
		{ id: 0, name: t('user.normal') },
		{ id: 1, name: t('user.freeze') },
	];

	return (
		<>
			<CardContent sx={{ padding: '24px' }}>
				{(() => {
					switch (type) {
						case FormType.view:
							return (
								<>
									<Box display='flex' justifyContent='space-between'>
										<Typography variant='h6'>{t('user.userData')}</Typography>
										<Button
											endIcon={<CreateOutlinedIcon />}
											color='secondary'
											onClick={() => {
												setType(FormType.edit);
											}}
										>
											{t('button.edit')}
										</Button>
									</Box>
									<Grid container spacing={3} marginTop={0} marginBottom={3}>
										<Grid item xs={6}>
											<AppFieldView
												label={t('user.userName')}
												value={userDetail?.username}
											/>
										</Grid>
										<Grid item xs={6}>
											<AppFieldView
												label={t('user.nickName')}
												value={userDetail?.nickname}
											/>
										</Grid>
										<Grid item xs={6}>
											<AppFieldView
												label={t('user.sex')}
												value={showLabel(userDetail?.gender, sexList)}
											/>
										</Grid>
										<Grid item xs={6}>
											<AppFieldView
												label={t('user.ageGroup')}
												value={showLabel(userDetail?.ageGroup, ageList)}
											/>
										</Grid>
										<Grid item xs={6}>
											<AppFieldView
												label={t('user.birthday')}
												value={`${userDetail?.monthOfBirth || '-'}${t(
													'common.month'
												)}${userDetail?.dayOfBirth || '-'}${t('common.day')}`}
											/>
										</Grid>
										<Grid item xs={6}>
											<AppFieldView
												label={t('user.isMeditation')}
												value={
													booleanMap[
														userDetail?.hasMeditationHabit === true
															? 'true'
															: 'false'
													]
												}
											/>
										</Grid>
										<Grid item xs={6}>
											<AppFieldView
												label={t('user.isReceiveEmail')}
												value={
													booleanMap[
														userDetail?.receivePromotion === true
															? 'true'
															: 'false'
													]
												}
											/>
										</Grid>
										<Grid item xs={12}>
											<Typography variant='body2' color='common.neutral40'>
												{t('user.customAvatar_')}
											</Typography>
											<Box display={'flex'} alignItems={'center'}>
												<img
													src={userDetail?.avatar?.url}
													width={80}
													height={80}
													style={{ borderRadius: '50%' }}
												/>
											</Box>
										</Grid>
									</Grid>
								</>
							);
						case FormType.edit:
							return (
								<EditUserInfoForm
									type={type}
									setType={setType}
									userDetail={userDetail}
								/>
							);
					}
				})()}
				<Divider />
				<Box
					display='flex'
					justifyContent='space-between'
					sx={{ paddingTop: '24px' }}
				>
					<Typography variant='h6'>{t('user.contactInfo')}</Typography>
				</Box>
				<Grid container spacing={3} marginTop={0} marginBottom={3}>
					<Grid item xs={6}>
						<AppFieldView
							label={t('user.email_')}
							value={userDetail?.email || t('user.nobind')}
							editButton={<ChangeEmailModal email={userDetail?.email || ''} />}
						/>
					</Grid>
					<Grid item xs={6}>
						<AppFieldView
							label={t('user.phoneNumber_')}
							value={userDetail?.phone || t('user.nobind')}
							editButton={
								<ChangePhoneModal
									formData={{
										phone: userDetail?.selectedPhone || '',
										countryCodeId: userDetail?.selectedCountryCodeId || '',
									}}
								/>
							}
						/>
					</Grid>
				</Grid>
				<Divider />
				<Box
					display='flex'
					justifyContent='space-between'
					sx={{ paddingTop: '24px' }}
				>
					<Typography variant='h6'>{t('user.appInfo')}</Typography>
				</Box>
				<Grid container spacing={3} marginTop={0} marginBottom={3}>
					<Grid item xs={6}>
						<AppFieldView
							label={t('user.userStatus')}
							valueElement={
								<Chip
									color={
										userDetail?.userStatus === 0 ? 'chipPrimary' : 'default'
									}
									label={showLabel(userDetail?.userStatus, statusList)}
								/>
							}
						/>
					</Grid>
					<Grid item xs={6}>
						<AppFieldView
							label={t('user.googleAccount')}
							value={
								userDetail?.googleId?.bind && userDetail?.googleId?.email
									? userDetail?.googleId?.email
									: userDetail?.googleId?.bind && !userDetail?.googleId?.email
									? t('user.binded')
									: t('user.nobind')
							}
						/>
					</Grid>
					<Grid item xs={6}>
						<AppFieldView
							label={t('user.faceBookAccount')}
							value={
								userDetail?.facebookId?.bind && userDetail?.facebookId?.email
									? userDetail?.facebookId?.email
									: userDetail?.facebookId?.bind &&
									  !userDetail?.facebookId?.email
									? t('user.binded')
									: t('user.nobind')
							}
						/>
					</Grid>
					<Grid item xs={6}>
						<AppFieldView
							label={t('user.appleAccount')}
							value={
								userDetail?.appleId?.bind && userDetail?.appleId?.email
									? userDetail?.appleId?.email
									: userDetail?.appleId?.bind && !userDetail?.appleId?.email
									? t('user.binded')
									: t('user.nobind')
							}
						/>
					</Grid>
					<Grid item xs={6}>
						<AppFieldView
							label={t('user.registrationTime')}
							value={
								!userDetail?.createdAt
									? '-'
									: dayjs(userDetail?.createdAt)
											.locale('en')
											.format(DISPLAY_DATETIME_12H_FORMAT)
							}
						/>
					</Grid>
					<Grid item xs={6}>
						<AppFieldView
							label={t('user.lastOnlinetime')}
							value={
								!userDetail?.lastLogin
									? '-'
									: dayjs(userDetail?.lastLogin)
											.locale('en')
											.format(DISPLAY_DATETIME_12H_FORMAT)
							}
						/>
					</Grid>
				</Grid>
			</CardContent>
		</>
	);
}
